import { findFontByFamily, getFontUrl, type FontRecord } from './fonts';
import { computeTextItemSizeFromLock, type ItemSizeLimits } from './itemSizing';
import { measureFullSvgDocumentContentBounds, measureFullSvgDocumentPerimeter, type SvgContentBounds } from './pathLength';
import { PATH_UNIT_TO_MM } from './textMetrics';
import { buildTextDisplaySvg, type SvgTrimViewBox } from './svgDocument';
import { translatePathData } from './svgPathTransform';
import {
  buildTextPathLayoutAtFontSize,
  loadOpentypeFont,
  type TextPathLayout,
} from './textMetrics';
import type { TextItem } from '../Reducers/Configurator/types';

export type TextLayoutFields = {
  pathData: string;
  pathOffsetX: number;
  pathOffsetY: number;
  trimViewBoxX: number;
  trimViewBoxY: number;
  viewBoxWidth: number;
  viewBoxHeight: number;
  baseWidth: number;
  baseHeight: number;
  sourcePerimeter: number;
};

function pathUnitsToNaturalMm(viewBoxWidth: number, viewBoxHeight: number) {
  return {
    baseWidth: viewBoxWidth * PATH_UNIT_TO_MM,
    baseHeight: viewBoxHeight * PATH_UNIT_TO_MM,
  };
}

function trimFromContentBounds(
  layout: TextPathLayout,
  bounds: SvgContentBounds | null
): SvgTrimViewBox {
  const fallback = {
    x: 0,
    y: 0,
    width: layout.viewBoxWidth,
    height: layout.viewBoxHeight,
  };
  if (!bounds || !(bounds.width > 0) || !(bounds.height > 0)) {
    return fallback;
  }
  return {
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
  };
}

async function layoutToFields(layout: TextPathLayout): Promise<TextLayoutFields> {
  const pathData = translatePathData(layout.pathData, layout.offsetX, layout.offsetY);

  const initialSvg = buildTextDisplaySvg({
    pathData,
    viewBoxWidth: layout.viewBoxWidth,
    viewBoxHeight: layout.viewBoxHeight,
  });
  const bounds = measureFullSvgDocumentContentBounds(
    initialSvg,
    layout.viewBoxWidth,
    layout.viewBoxHeight
  );
  const trim = trimFromContentBounds(layout, bounds);

  const displaySvg = buildTextDisplaySvg({
    pathData,
    trimViewBoxX: trim.x,
    trimViewBoxY: trim.y,
    viewBoxWidth: trim.width,
    viewBoxHeight: trim.height,
  });
  const sourcePerimeter = await measureFullSvgDocumentPerimeter(displaySvg, trim.width, trim.height);
  const natural = pathUnitsToNaturalMm(trim.width, trim.height);

  return {
    pathData,
    pathOffsetX: 0,
    pathOffsetY: 0,
    trimViewBoxX: trim.x,
    trimViewBoxY: trim.y,
    viewBoxWidth: trim.width,
    viewBoxHeight: trim.height,
    baseWidth: natural.baseWidth,
    baseHeight: natural.baseHeight,
    sourcePerimeter,
  };
}

export async function buildTextLayoutFields(
  content: string,
  fontFamily: string,
  fonts: FontRecord[]
): Promise<TextLayoutFields | null> {
  const fontRecord = findFontByFamily(fonts, fontFamily);
  const fontUrl = fontRecord ? getFontUrl(fontRecord) : null;
  if (!fontUrl) return null;

  const font = await loadOpentypeFont(fontUrl);
  if (!font) return null;

  return layoutToFields(buildTextPathLayoutAtFontSize(font, content));
}

/** Apply path layout and recompute locked dimensions, keeping the item centered. */
export function applyTextLayoutToItem(
  item: TextItem,
  layout: TextLayoutFields,
  limits?: ItemSizeLimits
): Partial<TextItem> {
  const { width, height, scale } = computeTextItemSizeFromLock(
    item,
    layout.baseWidth,
    layout.baseHeight,
    limits
  );

  return {
    pathData: layout.pathData,
    pathOffsetX: layout.pathOffsetX,
    pathOffsetY: layout.pathOffsetY,
    trimViewBoxX: layout.trimViewBoxX,
    trimViewBoxY: layout.trimViewBoxY,
    viewBoxWidth: layout.viewBoxWidth,
    viewBoxHeight: layout.viewBoxHeight,
    baseWidth: layout.baseWidth,
    baseHeight: layout.baseHeight,
    sourcePerimeter: layout.sourcePerimeter,
    width,
    height,
    scale,
    x: item.x + (item.width - width) / 2,
    y: item.y + (item.height - height) / 2,
  };
}

export async function resolveTextItemLayout(
  item: TextItem,
  fonts: FontRecord[],
  limits?: ItemSizeLimits
): Promise<Partial<TextItem>> {
  const layout = await buildTextLayoutFields(item.content, item.fontFamily, fonts);
  if (!layout) return {};
  return applyTextLayoutToItem(item, layout, limits);
}
